import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'ducks/hooks'
import useStyles from './styles'
import { Typography, Button, TextField } from 'components/UI'
import Modal from 'components/UI/CustomUI/molecules/Modal'
import { validateEmail } from 'helpers'
import { getUser, getUserPrimaryEmail } from 'ducks/selectors'
import { userActions } from 'ducks/actions'
import { toast } from 'react-toastify'

type Props = {}

const DangerZone = (props: Props) => {
    const classes = useStyles()
    const currentEmail = useSelector(getUserPrimaryEmail)
    const dispatch = useAppDispatch()
    const [newEmail, setNewEmail] = useState('')
    const [emailError, setEmailError] = useState('')
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false)
    const user = useSelector(getUser)

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value
        setNewEmail(email)

        // Validate email in real time
        if (email.trim() === '') {
            setEmailError('')
        } else if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address')
        } else if (email.toLowerCase() === currentEmail.toLowerCase()) {
            setEmailError('New email must be different from your current email')
        } else {
            setEmailError('')
        }
    }

    const handleSendClick = () => {
        if (newEmail.trim() && validateEmail(newEmail) && newEmail.toLowerCase() !== currentEmail.toLowerCase()) {
            setShowConfirmationModal(true)
        }
    }

    const handleFirstConfirmation = () => {
        setShowConfirmationModal(false)
        setShowFinalConfirmationModal(true)
    }

    const handleFinalConfirmation = () => {
        dispatch(userActions.changeUserEmail({
            email: newEmail,
            userId: user.id,
        }, (success: boolean, errorCode?: string) => {
            if (success) {
                toast.success('Email changed successfully')
                setShowFinalConfirmationModal(false)
                setNewEmail('')
                setEmailError('')
            } else {
                if (errorCode === 'PL-0001') {
                    toast.error('Email already in use')
                } else {
                    toast.error('Failed to change email')
                }
            }
        }))

    }

    const handleCloseModal = () => {
        setShowConfirmationModal(false)
        setShowFinalConfirmationModal(false)
    }

    const firstModalActions = [
        {
            textButton: 'Cancel',
            variant: 'outlined' as const,
            onClick: handleCloseModal,
        },
        {
            textButton: 'Continue',
            variant: 'contained' as const,
            onClick: handleFirstConfirmation,
        },
    ]

    const finalModalActions = [
        {
            textButton: 'Cancel',
            variant: 'outlined' as const,
            onClick: handleCloseModal,
        },
        {
            textButton: 'Send',
            variant: 'contained' as const,
            onClick: handleFinalConfirmation,
        },
    ]

    const isEmailValid = newEmail.trim() !== '' &&
        validateEmail(newEmail) &&
        newEmail.toLowerCase() !== currentEmail.toLowerCase()

    return (
        <div className={classes.root}>
            <Typography className={classes.title}>Danger Zone</Typography>

            <div className={classes.emailSection}>
                <Typography className={classes.sectionTitle}>Change Email</Typography>
                <Typography className={classes.description}>
                    Enter your new email to update your account
                </Typography>

                {currentEmail && currentEmail !== 'No email registered' && (
                    <Typography className={classes.currentEmail}>
                        Current email: <strong>{currentEmail}</strong>
                    </Typography>
                )}

                <div className={classes.inputContainer}>
                    <TextField
                        type="email"
                        value={newEmail}
                        onChange={handleEmailChange}
                        placeholder="new@email.com"
                        fullWidth
                        className={classes.emailInput}
                        error={!!emailError}
                        helperText={emailError}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendClick}
                        disabled={!isEmailValid}
                        className={classes.sendButton}
                    >
                        Send
                    </Button>
                </div>
            </div>

            {/* First confirmation modal */}
            <Modal
                open={showConfirmationModal}
                setOpen={setShowConfirmationModal}
                title="Confirm change of email"
                size="sm"
                actions={firstModalActions}
            >
                <div className={classes.modalContent}>
                    <Typography className={classes.modalText}>
                        Are you sure you want to change your email from <strong>{currentEmail}</strong> to <strong>{newEmail}</strong>?
                    </Typography>
                    <Typography className={classes.modalWarning}>
                        This action cannot be undone.
                    </Typography>
                </div>
            </Modal>

            {/* Final confirmation modal */}
            <Modal
                open={showFinalConfirmationModal}
                setOpen={setShowFinalConfirmationModal}
                title="Final confirmation"
                size="sm"
                actions={finalModalActions}
            >
                <div className={classes.modalContent}>
                    <Typography className={classes.modalText}>
                        Are you completely sure? This is your last chance to cancel.
                    </Typography>
                    <Typography className={classes.modalWarning}>
                        Your email will be changed from <strong>{currentEmail}</strong> to <strong>{newEmail}</strong>
                    </Typography>
                </div>
            </Modal>
        </div>
    )
}

export default DangerZone